import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { EmailAutomationPresentationComponent } from "@shared/msoffice";
import { apiClient } from "../../utils/apiClient";
import type { GetEmailAutomationResponse } from "@services/demo/msoffice/types";

interface PipelinePresentationDrawerProps {
  open: boolean;
  onClose: () => void;
  emailId?: string;
  messageId?: string;
  sharedInbox?: string;
  inboxEmail?: string;
}

export function PipelinePresentationDrawer({
  open,
  onClose,
  emailId,
  messageId,
  sharedInbox,
  inboxEmail,
}: PipelinePresentationDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GetEmailAutomationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!emailId && !messageId) return;

    const params = new URLSearchParams();
    if (emailId) {
      params.set("emailId", emailId);
    } else if (messageId) {
      params.set("officeMessageId", messageId);
      if (sharedInbox) {
        params.set("sharedInbox", sharedInbox);
      }
    }
    if (inboxEmail) {
      params.set("inboxEmail", inboxEmail);
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);

    apiClient
      .get<GetEmailAutomationResponse>(
        `/email-automation/result?${params.toString()}`,
      )
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to fetch pipeline results");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, emailId, messageId, sharedInbox, inboxEmail]);

  const sections = result?.status === "ok" ? result.data?.sections ?? [] : [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": { width: { xs: "100%", md: 600 } },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Pipeline Results</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={32} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              Loading pipeline results...
            </Typography>
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : result?.status === "not_found" ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No pipeline results found for this email.
          </Typography>
        ) : (
          <EmailAutomationPresentationComponent
            sections={sections}
          />
        )}
      </Box>
    </Drawer>
  );
}
